import React from 'react';
import { Box, Button, IconButton, Tooltip, useTheme, alpha, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { ClientField } from 'shared/types/appTypes';

interface FormNavigationProps {
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  hasValue: boolean;
  isSaving?: boolean;
  currentField?: ClientField;
  showingResponse?: boolean;
  isFinalized?: boolean;
  isAutoPopulated?: boolean;
}

const FormNavigation: React.FC<FormNavigationProps> = ({
  onNext,
  onSkip,
  onBack,
  isFirstQuestion,
  isLastQuestion,
  hasValue,
  isSaving = false,
  currentField,
  showingResponse = false,
  isFinalized = false,
  isAutoPopulated = false,
}) => {
  const theme = useTheme();
  const isVideoField = currentField?.type === 'video';
  const showSaveButton = (!isVideoField || isLastQuestion) && !showingResponse && !isFinalized;

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        py: 2,
        borderTop: `1px solid ${theme.palette.divider}`
      }}
    >
      {/* Back button */}
      <Box sx={{ width: { xs: 40, sm: 48 } }}>
        {!isFirstQuestion && (
          <Tooltip title="Previous question">
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Save / Finish button - centered */}
      <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
        {showSaveButton && (
          <Button
            onClick={onNext}
            variant="contained"
            disabled={isSaving}
            endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : isLastQuestion ? <CheckIcon /> : null}
            size="small"
          >
            {isSaving ? 'Saving...' : isLastQuestion ? 'Finish' : isAutoPopulated ? 'Confirmed - Next Question' : 'Save and continue'}
          </Button>
        )}
      </Box>

      {/* Skip / Forward button */}
      <Box sx={{ width: { xs: 60, sm: 72 } }}>
        {!isLastQuestion && (
          <Button
            onClick={onSkip}
            size="small"
            disabled={isSaving}
            endIcon={<ArrowForwardIcon />}
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            {showSaveButton && !hasValue ? 'Skip for Now' : 'Next'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default FormNavigation;
